import React, { useState, useEffect, useCallback } from "react";
import {
  Share2,
  Facebook,
  Instagram,
  Linkedin,
  MapPin,
  Send,
  Clock,
  CheckCircle,
  XCircle,
  Loader,
  Plus,
  Sparkles,
  Calendar,
  BarChart3,
  Edit3,
  Trash2,
  Zap,
  Eye,
  ChevronLeft,
  ChevronRight,
  Settings,
  RefreshCw,
  Link,
  X,
  MousePointerClick,
  Users,
  TrendingUp,
} from "lucide-react";
import api from "../api/client";

// ─── Constants ───────────────────────────────────────────────────────────

const TABS = [
  { key: "posts", label: "Posts", icon: Send },
  { key: "compose", label: "Compose", icon: Edit3 },
  { key: "calendar", label: "Calendar", icon: Calendar },
  { key: "analytics", label: "Analytics", icon: BarChart3 },
  { key: "accounts", label: "Accounts", icon: Settings },
];

const PLATFORMS = [
  { key: "gbp", label: "GBP", color: "#4285f4", icon: MapPin },
  { key: "facebook", label: "Facebook", color: "#1877f2", icon: Facebook },
  { key: "instagram", label: "Instagram", color: "#e1306c", icon: Instagram },
  { key: "linkedin", label: "LinkedIn", color: "#0a66c2", icon: Linkedin },
];

const POST_STATUS = {
  draft: { label: "Draft", color: "text-gray-400", bg: "bg-gray-800/40" },
  scheduled: { label: "Scheduled", color: "text-blue-400", bg: "bg-blue-900/20" },
  published: { label: "Published", color: "text-green-400", bg: "bg-green-900/20" },
  failed: { label: "Failed", color: "text-red-400", bg: "bg-red-900/20" },
  cancelled: { label: "Cancelled", color: "text-gray-500", bg: "bg-gray-800/40" },
};

const PLATFORM_LIMITS = { gbp: 1500, facebook: 63206, instagram: 2200, linkedin: 3000 };

// ─── Platform Connection Cards ──────────────────────────────────────────

function PlatformCards({ siteId, accounts, onRefresh }) {
  const [connecting, setConnecting] = useState("");

  const handleConnect = async (platform) => {
    if (platform === "gbp") {
      window.location.href = "/google-business";
      return;
    }
    setConnecting(platform);
    try {
      const res = await api.post(`/websites/${siteId}/social/oauth/connect/`, { platform });
      window.location.href = res.data.auth_url;
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to connect");
      setConnecting("");
    }
  };

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
      {PLATFORMS.map(({ key, label, color, icon: Icon }) => {
        const acct = accounts.find((a) => a.platform === key && a.is_active);
        const connected = key === "gbp" ? null : !!acct;
        return (
          <div
            key={key}
            className="bg-dark-200 rounded-xl border border-dark-50 p-4 flex flex-col gap-2"
          >
            <div className="flex items-center gap-2">
              <div
                className="w-8 h-8 rounded-lg flex items-center justify-center"
                style={{ backgroundColor: color + "20" }}
              >
                <Icon size={16} style={{ color }} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-white">{label}</div>
                <div className="text-[10px] text-gray-500 truncate">
                  {key === "gbp"
                    ? "Via GBP module"
                    : acct
                    ? acct.account_name || "Connected"
                    : "Not connected"}
                </div>
              </div>
              <div
                className={`w-2 h-2 rounded-full ${
                  key === "gbp"
                    ? "bg-blue-400"
                    : connected
                    ? "bg-green-400"
                    : "bg-gray-600"
                }`}
              />
            </div>
            {key !== "gbp" && (
              <button
                onClick={() => handleConnect(key)}
                disabled={connecting === key}
                className={`text-xs px-3 py-1.5 rounded-lg font-medium transition-colors ${
                  connected
                    ? "border border-dark-50 text-gray-400 hover:text-white"
                    : "bg-copper/10 text-copper hover:bg-copper/20"
                }`}
              >
                {connecting === key ? (
                  <Loader size={12} className="animate-spin mx-auto" />
                ) : connected ? (
                  "Reconnect"
                ) : (
                  "Connect"
                )}
              </button>
            )}
            {key === "gbp" && (
              <a
                href="/google-business"
                className="text-xs px-3 py-1.5 rounded-lg font-medium border border-dark-50 text-gray-400 hover:text-white text-center transition-colors"
              >
                Manage in GBP
              </a>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─── Posts Tab ───────────────────────────────────────────────────────────

function PostsTab({ siteId, posts, loading, onRefresh }) {
  const [filter, setFilter] = useState("all");

  const filtered = posts.filter((p) => {
    if (filter === "all") return true;
    if (["gbp", "facebook", "instagram", "linkedin"].includes(filter))
      return p.platform === filter;
    return p.status === filter;
  });

  const handlePublishNow = async (postId) => {
    try {
      await api.post(`/websites/${siteId}/social/posts/${postId}/publish-now/`);
      onRefresh();
    } catch {
      // handled
    }
  };

  const handleDelete = async (postId) => {
    if (!window.confirm("Cancel this post?")) return;
    try {
      await api.delete(`/websites/${siteId}/social/posts/${postId}/`);
      onRefresh();
    } catch {
      // handled
    }
  };

  const filters = [
    "all", "gbp", "facebook", "instagram", "linkedin",
    "scheduled", "published", "draft",
  ];

  return (
    <div>
      {/* Filters */}
      <div className="flex flex-wrap gap-2 mb-4">
        {filters.map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
              filter === f
                ? "bg-copper/10 text-copper border border-copper/30"
                : "bg-dark-200 text-gray-400 border border-dark-50 hover:text-white"
            }`}
          >
            {f.charAt(0).toUpperCase() + f.slice(1)}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-16">
          <Loader size={24} className="animate-spin text-copper" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 text-gray-500 text-sm">
          No posts found. Go to the Compose tab to create one.
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((post) => {
            const st = POST_STATUS[post.status] || POST_STATUS.draft;
            const plat = PLATFORMS.find((p) => p.key === post.platform);
            return (
              <div
                key={post.id}
                className="bg-dark-200 rounded-xl border border-dark-50 p-4"
              >
                <div className="flex items-start gap-3">
                  <div className="flex-1 min-w-0">
                    {/* Badges */}
                    <div className="flex flex-wrap gap-1.5 mb-2">
                      <span className={`${st.bg} ${st.color} px-2 py-0.5 rounded text-[10px] font-medium`}>
                        {st.label}
                      </span>
                      <span className="bg-dark-100 text-gray-300 px-2 py-0.5 rounded text-[10px] font-medium">
                        {post.post_type}
                      </span>
                      {plat && (
                        <span
                          className="px-2 py-0.5 rounded text-[10px] font-medium"
                          style={{
                            backgroundColor: plat.color + "20",
                            color: plat.color,
                          }}
                        >
                          {plat.label}
                        </span>
                      )}
                      {post.ai_generated && (
                        <span className="bg-purple-900/20 text-purple-400 px-2 py-0.5 rounded text-[10px] font-medium">
                          AI
                        </span>
                      )}
                    </div>
                    {/* Content preview */}
                    <p className="text-sm text-white mb-1 line-clamp-1 font-medium">
                      {post.content?.slice(0, 60) || "Untitled"}
                    </p>
                    <p className="text-xs text-gray-500 line-clamp-2">
                      {post.content?.slice(0, 160)}
                    </p>
                    {post.scheduled_at && (
                      <div className="flex items-center gap-1 mt-2 text-[10px] text-gray-500">
                        <Clock size={10} />
                        {new Date(post.scheduled_at).toLocaleString()}
                      </div>
                    )}
                  </div>
                  {/* Actions */}
                  <div className="flex items-center gap-1 shrink-0">
                    {post.status === "scheduled" && (
                      <button
                        onClick={() => handlePublishNow(post.id)}
                        className="p-2 rounded-lg hover:bg-dark-100 text-amber-400 transition-colors"
                        title="Publish now"
                      >
                        <Zap size={14} />
                      </button>
                    )}
                    {["draft", "scheduled"].includes(post.status) && (
                      <button
                        onClick={() => handleDelete(post.id)}
                        className="p-2 rounded-lg hover:bg-dark-100 text-red-400 transition-colors"
                        title="Cancel"
                      >
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Compose Tab ────────────────────────────────────────────────────────

function ComposeTab({ siteId, accounts, onRefresh }) {
  const [form, setForm] = useState({
    platform: "facebook",
    post_type: "standard",
    content: "",
    link_url: "",
    cta_type: "",
    scheduled_at: "",
  });
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [repurposing, setRepurposing] = useState("");
  const [previewPlatform, setPreviewPlatform] = useState("facebook");

  const charLimit = PLATFORM_LIMITS[form.platform] || 2000;
  const charCount = form.content.length;

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const res = await api.post(`/websites/${siteId}/social/ai/generate/`, {
        platform: form.platform,
        post_type: form.post_type,
      });
      setForm((f) => ({
        ...f,
        content: res.data.content || "",
      }));
    } catch (err) {
      alert("AI generation failed: " + (err.response?.data?.detail || err.message));
    }
    setGenerating(false);
  };

  const handleRepurpose = async (toPlatform) => {
    if (!form.content.trim()) return;
    setRepurposing(toPlatform);
    try {
      const res = await api.post(`/websites/${siteId}/social/ai/repurpose/`, {
        content: form.content,
        from_platform: form.platform,
        to_platform: toPlatform,
      });
      setForm((f) => ({
        ...f,
        platform: toPlatform,
        content: res.data.content || f.content,
      }));
      setPreviewPlatform(toPlatform);
    } catch {
      // handled
    }
    setRepurposing("");
  };

  const handleSave = async (publishNow = false) => {
    if (!form.content.trim()) return;
    setSaving(true);
    try {
      const payload = {
        platform: form.platform,
        post_type: form.post_type,
        content: form.content,
        link_url: form.link_url || "",
        cta_type: form.cta_type || "",
        ai_generated: false,
      };
      if (form.scheduled_at) {
        payload.scheduled_at = new Date(form.scheduled_at).toISOString();
      }

      const res = await api.post(`/websites/${siteId}/social/posts/`, payload);

      if (publishNow && res.data.id) {
        await api.post(`/websites/${siteId}/social/posts/${res.data.id}/publish-now/`);
      }

      setForm({ platform: "facebook", post_type: "standard", content: "", link_url: "", cta_type: "", scheduled_at: "" });
      onRefresh();
    } catch (err) {
      alert("Save failed: " + (err.response?.data?.detail || err.message));
    }
    setSaving(false);
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Left: Editor */}
      <div className="space-y-4">
        {/* Platform selector */}
        <div>
          <label className="block text-xs text-gray-500 mb-1.5">Platform</label>
          <div className="flex gap-2">
            {PLATFORMS.map(({ key, label, color, icon: Icon }) => (
              <button
                key={key}
                onClick={() => { setForm((f) => ({ ...f, platform: key })); setPreviewPlatform(key); }}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium transition-colors border ${
                  form.platform === key
                    ? "border-copper/30 bg-copper/10 text-copper"
                    : "border-dark-50 bg-dark-200 text-gray-400 hover:text-white"
                }`}
              >
                <Icon size={14} />
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* Post type (only for GBP) */}
        {form.platform === "gbp" && (
          <div>
            <label className="block text-xs text-gray-500 mb-1.5">Post Type</label>
            <select
              value={form.post_type}
              onChange={(e) => setForm((f) => ({ ...f, post_type: e.target.value }))}
              className="w-full bg-dark-100 border border-dark-50 rounded-lg px-3 py-2 text-sm text-white focus:border-copper outline-none"
            >
              <option value="standard">Standard</option>
              <option value="offer">Offer</option>
              <option value="event">Event</option>
              <option value="product">Product</option>
            </select>
          </div>
        )}

        {/* AI Generate bar */}
        <div className="bg-purple-900/10 border border-purple-800/30 rounded-lg p-3 flex items-center gap-3">
          <Sparkles size={16} className="text-purple-400 shrink-0" />
          <span className="text-xs text-purple-300 flex-1">Generate with Brand Voice</span>
          <button
            onClick={handleGenerate}
            disabled={generating}
            className="px-3 py-1.5 bg-purple-600/20 text-purple-300 rounded-lg text-xs font-medium hover:bg-purple-600/30 transition-colors disabled:opacity-40"
          >
            {generating ? <Loader size={12} className="animate-spin" /> : "Generate"}
          </button>
        </div>

        {/* Content textarea */}
        <div>
          <label className="block text-xs text-gray-500 mb-1.5">Content</label>
          <textarea
            value={form.content}
            onChange={(e) => setForm((f) => ({ ...f, content: e.target.value }))}
            rows={8}
            maxLength={charLimit}
            className="w-full bg-dark-100 border border-dark-50 rounded-lg px-3 py-2 text-sm text-white focus:border-copper outline-none resize-none"
            placeholder="Write your post content..."
          />
          <div className={`text-right text-[10px] mt-1 ${charCount > charLimit * 0.9 ? "text-red-400" : "text-gray-500"}`}>
            {charCount.toLocaleString()} / {charLimit.toLocaleString()}
          </div>
        </div>

        {/* Link URL */}
        <div>
          <label className="block text-xs text-gray-500 mb-1.5">Link URL</label>
          <div className="flex items-center gap-2">
            <Link size={14} className="text-gray-600 shrink-0" />
            <input
              type="url"
              value={form.link_url}
              onChange={(e) => setForm((f) => ({ ...f, link_url: e.target.value }))}
              className="flex-1 bg-dark-100 border border-dark-50 rounded-lg px-3 py-2 text-sm text-white focus:border-copper outline-none"
              placeholder="https://..."
            />
          </div>
        </div>

        {/* CTA Type */}
        <div>
          <label className="block text-xs text-gray-500 mb-1.5">Call to Action</label>
          <select
            value={form.cta_type}
            onChange={(e) => setForm((f) => ({ ...f, cta_type: e.target.value }))}
            className="w-full bg-dark-100 border border-dark-50 rounded-lg px-3 py-2 text-sm text-white focus:border-copper outline-none"
          >
            <option value="">None</option>
            <option value="learn_more">Learn More</option>
            <option value="book">Book</option>
            <option value="order">Order</option>
            <option value="buy">Buy</option>
            <option value="sign_up">Sign Up</option>
            <option value="call">Call</option>
          </select>
        </div>

        {/* Schedule */}
        <div>
          <label className="block text-xs text-gray-500 mb-1.5">Schedule</label>
          <input
            type="datetime-local"
            value={form.scheduled_at}
            onChange={(e) => setForm((f) => ({ ...f, scheduled_at: e.target.value }))}
            className="w-full bg-dark-100 border border-dark-50 rounded-lg px-3 py-2 text-sm text-white focus:border-copper outline-none"
          />
        </div>

        {/* Action buttons */}
        <div className="flex gap-2">
          <button
            onClick={() => handleSave(false)}
            disabled={saving || !form.content.trim()}
            className="flex-1 px-4 py-2.5 bg-copper/10 text-copper rounded-lg text-sm font-medium hover:bg-copper/20 transition-colors disabled:opacity-40"
          >
            {form.scheduled_at ? "Schedule" : "Save Draft"}
          </button>
          <button
            onClick={() => handleSave(true)}
            disabled={saving || !form.content.trim()}
            className="px-4 py-2.5 bg-copper text-white rounded-lg text-sm font-medium hover:bg-copper/90 transition-colors disabled:opacity-40"
          >
            {saving ? <Loader size={14} className="animate-spin" /> : "Publish Now"}
          </button>
        </div>
      </div>

      {/* Right: Preview + Repurpose */}
      <div className="space-y-4">
        <div className="sticky top-4">
          {/* Preview tabs */}
          <div className="flex gap-1 mb-3">
            {PLATFORMS.map(({ key, label, color }) => (
              <button
                key={key}
                onClick={() => setPreviewPlatform(key)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                  previewPlatform === key
                    ? "text-white"
                    : "text-gray-500 hover:text-gray-300"
                }`}
                style={previewPlatform === key ? { backgroundColor: color + "20", color } : {}}
              >
                {label}
              </button>
            ))}
          </div>

          {/* Mock preview card */}
          <div className="bg-white rounded-xl p-4 min-h-[200px]">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-10 h-10 rounded-full bg-gray-200" />
              <div>
                <div className="text-sm font-semibold text-gray-900">Your Business</div>
                <div className="text-[10px] text-gray-500">Just now</div>
              </div>
            </div>
            <p className="text-sm text-gray-800 whitespace-pre-wrap">
              {form.content || "Your post content will appear here..."}
            </p>
            {form.link_url && (
              <div className="mt-3 border border-gray-200 rounded-lg p-2 text-xs text-blue-600 truncate">
                {form.link_url}
              </div>
            )}
          </div>

          {/* Repurpose strip */}
          {form.content.trim() && (
            <div className="mt-3 bg-dark-200 rounded-xl border border-dark-50 p-3">
              <div className="text-[10px] text-gray-500 mb-2">Repurpose for:</div>
              <div className="flex gap-2">
                {PLATFORMS.filter((p) => p.key !== form.platform).map(({ key, label, color, icon: Icon }) => (
                  <button
                    key={key}
                    onClick={() => handleRepurpose(key)}
                    disabled={!!repurposing}
                    className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[10px] font-medium border border-dark-50 text-gray-400 hover:text-white transition-colors disabled:opacity-40"
                  >
                    {repurposing === key ? (
                      <Loader size={10} className="animate-spin" />
                    ) : (
                      <Icon size={10} />
                    )}
                    {label}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Calendar Tab ───────────────────────���───────────────────────────────

function CalendarTab({ posts }) {
  const [currentDate, setCurrentDate] = useState(new Date());

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const monthName = currentDate.toLocaleString("default", { month: "long", year: "numeric" });

  const days = [];
  for (let i = 0; i < firstDay; i++) days.push(null);
  for (let i = 1; i <= daysInMonth; i++) days.push(i);

  const getPostsForDay = (day) => {
    if (!day) return [];
    const dateStr = `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
    return posts.filter((p) => {
      const d = p.scheduled_at || p.created_at;
      return d && d.startsWith(dateStr);
    });
  };

  const prev = () => setCurrentDate(new Date(year, month - 1, 1));
  const next = () => setCurrentDate(new Date(year, month + 1, 1));

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <button onClick={prev} className="p-2 rounded-lg hover:bg-dark-200 text-gray-400 transition-colors">
          <ChevronLeft size={16} />
        </button>
        <h3 className="text-sm font-medium text-white">{monthName}</h3>
        <button onClick={next} className="p-2 rounded-lg hover:bg-dark-200 text-gray-400 transition-colors">
          <ChevronRight size={16} />
        </button>
      </div>

      {/* Day labels */}
      <div className="grid grid-cols-7 gap-1 mb-1">
        {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
          <div key={d} className="text-[10px] text-gray-600 text-center py-1 font-medium">
            {d}
          </div>
        ))}
      </div>

      {/* Day cells */}
      <div className="grid grid-cols-7 gap-1">
        {days.map((day, idx) => {
          const dayPosts = getPostsForDay(day);
          const isToday =
            day &&
            year === new Date().getFullYear() &&
            month === new Date().getMonth() &&
            day === new Date().getDate();
          return (
            <div
              key={idx}
              className={`min-h-[80px] rounded-lg p-1.5 border ${
                day
                  ? isToday
                    ? "border-copper/30 bg-copper/5"
                    : "border-dark-50 bg-dark-200"
                  : "border-transparent"
              }`}
            >
              {day && (
                <>
                  <div className={`text-[10px] font-medium mb-1 ${isToday ? "text-copper" : "text-gray-500"}`}>
                    {day}
                  </div>
                  <div className="space-y-0.5">
                    {dayPosts.slice(0, 3).map((p) => {
                      const plat = PLATFORMS.find((pl) => pl.key === p.platform);
                      return (
                        <div
                          key={p.id}
                          className="text-[8px] px-1 py-0.5 rounded truncate"
                          style={{
                            backgroundColor: (plat?.color || "#666") + "20",
                            color: plat?.color || "#999",
                          }}
                          title={p.content?.slice(0, 80)}
                        >
                          {p.content?.slice(0, 20) || plat?.label}
                        </div>
                      );
                    })}
                    {dayPosts.length > 3 && (
                      <div className="text-[8px] text-gray-600">+{dayPosts.length - 3} more</div>
                    )}
                  </div>
                </>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Analytics Tab ──────────────────────────────────────────────────────

function AnalyticsTab({ siteId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/websites/${siteId}/social/analytics/`);
      setData(res.data);
    } catch {
      // handled
    }
    setLoading(false);
  }, [siteId]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <Loader size={24} className="animate-spin text-copper" />
      </div>
    );
  }

  if (!data) {
    return <div className="text-center py-16 text-gray-500 text-sm">No analytics data available yet.</div>;
  }

  const t = data.totals || {};
  const metrics = [
    { label: "Impressions", value: t.total_impressions || 0, icon: Eye, color: "text-blue-400" },
    { label: "Clicks", value: t.total_clicks || 0, icon: MousePointerClick, color: "text-green-400" },
    { label: "Engagements", value: t.total_engagements || 0, icon: Users, color: "text-purple-400" },
    { label: "Published", value: data.published_count || 0, icon: Send, color: "text-copper" },
  ];

  return (
    <div>
      {/* Metric cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        {metrics.map(({ label, value, icon: Icon, color }) => (
          <div key={label} className="bg-dark-200 rounded-xl border border-dark-50 p-4">
            <div className="flex items-center gap-2 mb-2">
              <Icon size={14} className={color} />
              <span className="text-xs text-gray-500">{label}</span>
            </div>
            <div className="text-xl font-bold text-white">{value.toLocaleString()}</div>
          </div>
        ))}
      </div>

      {/* Platform breakdown */}
      {data.by_platform && data.by_platform.length > 0 && (
        <div className="bg-dark-200 rounded-xl border border-dark-50 p-4">
          <h4 className="text-sm font-medium text-white mb-3">Platform Breakdown</h4>
          <div className="space-y-3">
            {data.by_platform.map((row) => {
              const plat = PLATFORMS.find((p) => p.key === row.platform);
              const maxImpressions = Math.max(...data.by_platform.map((r) => r.impressions || 1));
              const pct = ((row.impressions || 0) / maxImpressions) * 100;
              return (
                <div key={row.platform} className="flex items-center gap-3">
                  <div className="w-20 text-xs text-gray-400">{plat?.label || row.platform}</div>
                  <div className="flex-1 h-2 bg-dark-100 rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all"
                      style={{
                        width: `${pct}%`,
                        backgroundColor: plat?.color || "#666",
                      }}
                    />
                  </div>
                  <div className="w-16 text-right text-xs text-gray-400">
                    {(row.impressions || 0).toLocaleString()}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Accounts Tab ───────────────────────────────────────────────────────

function AccountsTab({ siteId, accounts, onRefresh }) {
  const handleDisconnect = async (accountId) => {
    if (!window.confirm("Disconnect this account?")) return;
    try {
      await api.delete(`/websites/${siteId}/social/accounts/${accountId}/`);
      onRefresh();
    } catch {
      // handled
    }
  };

  const handleConnect = async (platform) => {
    if (platform === "gbp") {
      window.location.href = "/google-business";
      return;
    }
    try {
      const res = await api.post(`/websites/${siteId}/social/oauth/connect/`, { platform });
      window.location.href = res.data.auth_url;
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to initiate connection");
    }
  };

  return (
    <div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        {PLATFORMS.map(({ key, label, color, icon: Icon }) => {
          const platformAccounts = accounts.filter((a) => a.platform === key && a.is_active);
          return (
            <div key={key} className="bg-dark-200 rounded-xl border border-dark-50 p-4">
              <div className="flex items-center gap-3 mb-3">
                <div
                  className="w-10 h-10 rounded-xl flex items-center justify-center"
                  style={{ backgroundColor: color + "20" }}
                >
                  <Icon size={20} style={{ color }} />
                </div>
                <div>
                  <div className="text-sm font-medium text-white">{label}</div>
                  <div className="text-[10px] text-gray-500">
                    {key === "gbp"
                      ? "Managed via Google Business module"
                      : platformAccounts.length > 0
                      ? `${platformAccounts.length} account(s) connected`
                      : "Not connected"}
                  </div>
                </div>
              </div>

              {platformAccounts.map((acct) => (
                <div key={acct.id} className="flex items-center justify-between py-2 border-t border-dark-50">
                  <div>
                    <div className="text-xs text-white">{acct.account_name || acct.account_id}</div>
                    {acct.token_expires_at && (
                      <div className={`text-[10px] ${acct.is_token_expired ? "text-red-400" : "text-gray-500"}`}>
                        Token {acct.is_token_expired ? "expired" : `expires ${new Date(acct.token_expires_at).toLocaleDateString()}`}
                      </div>
                    )}
                  </div>
                  <button
                    onClick={() => handleDisconnect(acct.id)}
                    className="text-[10px] text-red-400 hover:text-red-300 px-2 py-1"
                  >
                    Disconnect
                  </button>
                </div>
              ))}

              <button
                onClick={() => handleConnect(key)}
                className="w-full mt-3 px-3 py-2 rounded-lg text-xs font-medium bg-copper/10 text-copper hover:bg-copper/20 transition-colors"
              >
                {platformAccounts.length > 0 ? "Reconnect" : "Connect"} {label}
              </button>
            </div>
          );
        })}
      </div>

      {/* Redirect URIs reference */}
      <div className="bg-dark-200 rounded-xl border border-dark-50 p-4">
        <h4 className="text-sm font-medium text-white mb-2">OAuth Redirect URIs</h4>
        <p className="text-[10px] text-gray-500 mb-3">Whitelist these URLs in each platform's developer portal:</p>
        <div className="space-y-1.5">
          {[
            { platform: "Meta (Facebook/Instagram)", uri: "https://app.gcmmgroup.com/api/social/oauth/facebook/callback/" },
            { platform: "LinkedIn", uri: "https://app.gcmmgroup.com/api/social/oauth/linkedin/callback/" },
          ].map(({ platform, uri }) => (
            <div key={platform} className="flex items-center gap-2 bg-dark-100 rounded-lg px-3 py-2">
              <span className="text-[10px] text-gray-500 shrink-0 w-36">{platform}</span>
              <code className="text-[10px] text-copper flex-1 truncate">{uri}</code>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Main Social Hub Component ──────────────────────────────────────────

export default function SocialHub({ selectedSite }) {
  const [tab, setTab] = useState("posts");
  const [posts, setPosts] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);

  const siteId = selectedSite?.id;

  const load = useCallback(async () => {
    if (!siteId) return;
    setLoading(true);
    try {
      const [postsRes, accountsRes] = await Promise.all([
        api.get(`/websites/${siteId}/social/posts/`),
        api.get(`/websites/${siteId}/social/accounts/`),
      ]);
      setPosts(postsRes.data.results || postsRes.data);
      setAccounts(accountsRes.data.accounts || []);
    } catch {
      // handled
    }
    setLoading(false);
  }, [siteId]);

  useEffect(() => { load(); }, [load]);

  // Check for connection success in URL
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("connected")) {
      load();
      window.history.replaceState({}, "", "/social");
    }
  }, [load]);

  if (!siteId) {
    return (
      <div className="flex items-center justify-center py-32 text-gray-500 text-sm">
        Select a website to manage social accounts.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-white">Social Hub</h1>
          <p className="text-xs text-gray-500 mt-0.5">
            Manage and schedule posts across all platforms
          </p>
        </div>
        <button
          onClick={load}
          className="p-2 rounded-lg hover:bg-dark-200 text-gray-400 transition-colors"
          title="Refresh"
        >
          <RefreshCw size={16} />
        </button>
      </div>

      {/* Platform connection cards */}
      <PlatformCards siteId={siteId} accounts={accounts} onRefresh={load} />

      {/* Tab bar */}
      <div className="flex border-b border-dark-50">
        {TABS.map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              tab === key
                ? "border-copper text-copper"
                : "border-transparent text-gray-500 hover:text-white"
            }`}
          >
            <Icon size={14} />
            {label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {tab === "posts" && (
        <PostsTab siteId={siteId} posts={posts} loading={loading} onRefresh={load} />
      )}
      {tab === "compose" && (
        <ComposeTab siteId={siteId} accounts={accounts} onRefresh={load} />
      )}
      {tab === "calendar" && <CalendarTab posts={posts} />}
      {tab === "analytics" && <AnalyticsTab siteId={siteId} />}
      {tab === "accounts" && (
        <AccountsTab siteId={siteId} accounts={accounts} onRefresh={load} />
      )}
    </div>
  );
}
